import { Autocomplete, Checkbox, Chip, FormControlLabel, Grid, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { MuiSelectWithFocus, MuiTextFieldWithFocus, StyledToggleButton, StyledToggleButtonGroup } from '../../config/MaterialUIConstants';
import { CONTACT_PREFRENCES } from '../../utility/Constant';
import { getPostalCodeError, isPostalCodeValid } from '../../utility/helpers/commonFunction';

const styles = {
    label: {
        fontWeight: 500,
        fontSize: '16px',
        lineHeight: '20px',
        marginBottom: '8px',
        color: '#212121',
    },
};

const AdditionalDetailsForm = ({ control, errors, setValue, setError, clearErrors, watch, discountList, cityList, provinceList, selectedCountry }) => {
    const watchedProvince = watch('province');

    // City Autocomplete Optimization (Chunked Loading)
    const [cityInputValue, setCityInputValue] = useState('');
    const [filteredCityOptions, setFilteredCityOptions] = useState([]);
    const [displayedCityOptions, setDisplayedCityOptions] = useState([]);
    const [cityLoadIndex, setCityLoadIndex] = useState(1);
    const itemsPerLoad = 100;

    // Filter and chunk city list
    useEffect(() => {
        if (!cityList?.length) {
            setFilteredCityOptions([]);
            setDisplayedCityOptions([]);
            return;
        }

        const searchTerm = cityInputValue.toLowerCase().trim();

        // If input is empty or too short, show all (chunked)
        if (searchTerm.length < 2) {
            setFilteredCityOptions(cityList);
            setDisplayedCityOptions(cityList.slice(0, itemsPerLoad * cityLoadIndex));
            return;
        }

        // Filter based on search
        const filtered = cityList.filter((city) => city.city?.toLowerCase().includes(searchTerm));

        setFilteredCityOptions(filtered);
        setDisplayedCityOptions(filtered.slice(0, itemsPerLoad * cityLoadIndex));
    }, [cityList, cityInputValue, cityLoadIndex]);

    const handleCityScroll = (event) => {
        const listboxNode = event.currentTarget;
        const bottomReached = listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 20;

        if (bottomReached && displayedCityOptions.length < filteredCityOptions.length) {
            setCityLoadIndex((prev) => prev + 1);
        }
    };

    return (
        <Grid item container xs={12} spacing={3}>
            {/* Company */}
            <Grid item xs={9} md={5.25}>
                <Typography sx={styles.label}>Company</Typography>
                <Controller name="company" control={control} render={({ field }) => <TextField {...field} fullWidth sx={MuiTextFieldWithFocus} />} />
            </Grid>

            {/* Discount Program */}
            <Grid item xs={12} md={5.25}>
                <Typography sx={styles.label}>Discount Program</Typography>
                <Controller
                    name="membershipProgram"
                    control={control}
                    render={({ field }) => (
                        <Select {...field} fullWidth displayEmpty sx={MuiSelectWithFocus} MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}>
                            <MenuItem value="none">None</MenuItem>
                            {discountList?.map((item) => (
                                <MenuItem key={item._id} value={item._id}>
                                    <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} sx={{ width: '100%' }}>
                                        <div> {item.programName}</div>
                                        <Chip
                                            sx={{
                                                borderRadius: '100px',
                                                height: '32px',
                                                background: 'rgba(0, 0, 0, 0.08)',
                                                color: 'rgba(0, 0, 0, 0.87)',
                                            }}
                                            label={`${item.discount}%`}
                                        />
                                    </Stack>
                                </MenuItem>
                            ))}
                        </Select>
                    )}
                />
            </Grid>

            <Grid item xs={3} md={1.5} display={'flex'} alignItems={'center'} mt={'25px'}>
                <Controller
                    name="isFleetCustomer"
                    control={control}
                    render={({ field }) => (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    {...field}
                                    checked={field.value}
                                    icon={
                                        <span
                                            style={{
                                                width: 20,
                                                height: 20,
                                                borderRadius: '4px',
                                                border: '2px solid #E6E6E6',
                                                backgroundColor: '#fff',
                                                display: 'inline-block',
                                                boxSizing: 'border-box',
                                                boxShadow: '0 2px 3px 0 rgba(0, 0, 0, 0.06)',
                                            }}
                                        />
                                    }
                                />
                            }
                            label="Fleet"
                            sx={{ mr: 0, whiteSpace: 'nowrap' }}
                        />
                    )}
                />
            </Grid>

            {/* Tax Exemption # */}
            <Grid item xs={12} md={5.25}>
                <Typography sx={styles.label}>Tax Exemption #</Typography>
                <Controller
                    name="taxExemptionNumber"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            fullWidth
                            sx={MuiTextFieldWithFocus}
                            error={!!errors.taxExemptionNumber}
                            helperText={errors.taxExemptionNumber?.message}
                        />
                    )}
                />
            </Grid>

            {/* Contact Preference */}
            <Grid item xs={12} md={6.75}>
                <Typography sx={styles.label}>Contact Preference</Typography>
                <Controller
                    name="contactPrefrence"
                    control={control}
                    render={({ field }) => (
                        <StyledToggleButtonGroup
                            value={field.value}
                            exclusive
                            onChange={(e, val) => field.onChange(val)}
                            aria-label="contact preference"
                            sx={{ gap: 2 }}
                        >
                            {CONTACT_PREFRENCES.map((preference) => {
                                // Custom label mapping for display only
                                const displayLabel =
                                    preference.key === 'callOnPhone'
                                        ? 'Phone Call'
                                        : preference.key === 'inAppMessages'
                                          ? 'Text'
                                          : preference.key === 'email'
                                            ? 'Email'
                                            : preference.value;

                                return (
                                    <StyledToggleButton key={preference.key} value={preference.key}>
                                        {displayLabel}
                                    </StyledToggleButton>
                                );
                            })}
                        </StyledToggleButtonGroup>
                    )}
                />
            </Grid>

            {/* Notifications */}
            {/*   <Grid item xs={12} md={6}>
                <Typography sx={styles.label}>Notifications</Typography>
                <Controller
                    name="notifications"
                    control={control}
                    render={({ field }) => (
                        <StyledToggleButtonGroup
                            value={field.value}
                            exclusive
                            onChange={(e, val) => field.onChange(val)}
                            aria-label="notifications"
                            sx={{ gap: 2 }}
                        >
                            <StyledToggleButton value="sms">SMS</StyledToggleButton>
                            <StyledToggleButton value="email">Email</StyledToggleButton>
                            <StyledToggleButton value="app">App</StyledToggleButton>
                        </StyledToggleButtonGroup>
                    )}
                />
            </Grid>*/}

            {/* Street Address */}
            <Grid item xs={12}>
                <Typography sx={styles.label}>Street Address</Typography>
                <Controller
                    name="streetAddress"
                    control={control}
                    render={({ field }) => (
                        <TextField {...field} fullWidth sx={MuiTextFieldWithFocus} error={!!errors.streetAddress} helperText={errors.streetAddress?.message} />
                    )}
                />
            </Grid>

            {/* City */}
            <Grid item xs={12} md={4}>
                <Typography sx={styles.label}>City</Typography>

                <Controller
                    name="city"
                    control={control}
                    render={({ field }) => (
                        <Autocomplete
                            forcePopupIcon
                            options={(displayedCityOptions || []).map((city) => ({
                                label: city.city,
                                value: city._id,
                                provinceLabel: city?.province?.province,
                            }))}
                            value={
                                !watchedProvince
                                    ? null
                                    : (cityList || []).map((city) => ({ label: city.city, value: city._id })).find((opt) => opt.value === field.value) || null
                            }
                            getOptionLabel={(option) => (typeof option === 'string' ? option : option.label || '')}
                            isOptionEqualToValue={(option, value) => option.value === value.value}
                            inputValue={cityInputValue}
                            onInputChange={(event, newInputValue) => {
                                setCityInputValue(newInputValue);
                                setCityLoadIndex(1); // Reset load index on search
                            }}
                            filterOptions={(x) => x} // Disable built-in filtering
                            ListboxProps={{
                                onScroll: handleCityScroll,
                                style: { maxHeight: 300, overflow: 'auto' },
                            }}
                            onChange={(e, newVal) => {
                                // Set city value
                                field.onChange(newVal?.value || '');

                                // Auto-populate province when city is selected
                                if (newVal?.value) {
                                    // Find the full city object to get province data
                                    const selectedCity = cityList?.find((city) => city._id === newVal.value);
                                    if (selectedCity?.province?._id) {
                                        setValue('province', selectedCity.province._id);
                                        clearErrors('province');
                                    }
                                    clearErrors('city');
                                }
                            }}
                            renderOption={(props, option, { index }) => {
                                const { key, ...otherProps } = props;
                                return (
                                    <li key={index} {...otherProps}>
                                        <span>
                                            {option.label}
                                            {option?.provinceLabel && ` - ${option.provinceLabel}`}
                                        </span>
                                    </li>
                                );
                            }}
                            renderInput={(params) => (
                                <TextField {...params} sx={MuiTextFieldWithFocus} error={!!errors.city} helperText={errors.city?.message} />
                            )}
                        />
                    )}
                />
            </Grid>

            {/* Province */}
            <Grid item xs={12} md={4}>
                <Typography sx={styles.label}>{selectedCountry?.country === 'United States' ? 'State' : 'Province'}</Typography>

                <Controller
                    name="province"
                    control={control}
                    render={({ field }) => (
                        <Autocomplete
                            forcePopupIcon
                            options={(provinceList || []).map((prov) => ({
                                label: prov.province,
                                value: prov._id,
                            }))}
                            value={
                                (provinceList || []).map((prov) => ({ label: prov.province, value: prov._id })).find((opt) => opt.value === field.value) || null
                            }
                            getOptionLabel={(option) => (typeof option === 'string' ? option : option.label || '')}
                            isOptionEqualToValue={(option, value) => option.value === value.value}
                            onChange={(e, newVal) => {
                                // Set province value
                                field.onChange(newVal?.value || '');

                                // Clear city when province changes (city depends on province)
                                setValue('city', '');
                                clearErrors('city');

                                // Clear province error
                                if (newVal) {
                                    clearErrors('province');
                                }
                            }}
                            renderOption={(props, option, { index }) => {
                                const { key, ...otherProps } = props;
                                return (
                                    <li key={index} {...otherProps}>
                                        <span>{option.label}</span>
                                    </li>
                                );
                            }}
                            renderInput={(params) => (
                                <TextField {...params} sx={MuiTextFieldWithFocus} error={!!errors.province} helperText={errors.province?.message} />
                            )}
                        />
                    )}
                />
            </Grid>

            {/* Postal Code */}
            <Grid item xs={12} md={4}>
                <Typography sx={styles.label}>{selectedCountry?.country === 'United States' ? 'Zip Code' : 'Postal Code'}</Typography>
                <Controller
                    name="postalCode"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            fullWidth
                            sx={MuiTextFieldWithFocus}
                            error={!!errors.postalCode}
                            helperText={errors.postalCode?.message}
                            inputProps={{
                                style: { textTransform: selectedCountry?.country === 'United States' ? 'none' : 'uppercase' },
                                maxLength: selectedCountry?.country === 'United States' ? 5 : 7,
                            }}
                            onChange={(e) => {
                                const input = e.target;
                                const rawValue = input.value;

                                const selectionStart = input.selectionStart || 0;
                                const prevValue = field.value || '';

                                // Country-specific processing
                                const isUSA = selectedCountry?.country === 'United States';
                                const cleaned = isUSA
                                    ? rawValue.replace(/\D/g, '') // USA: digits only
                                    : rawValue.replace(/\s/g, '').toUpperCase(); // Canada: remove spaces, uppercase

                                // Detect if user is deleting the space at index 3 (Canada only)
                                const isDeletingSpace = !isUSA && prevValue.charAt(selectionStart - 1) === ' ' && rawValue.length < prevValue.length;

                                // Format based on country
                                let formatted = cleaned;
                                if (!isUSA && cleaned.length > 3) {
                                    formatted = cleaned.slice(0, 3) + ' ' + cleaned.slice(3, 6);
                                }

                                // Update form state
                                setValue('postalCode', formatted);

                                // Country-specific validation
                                if (!formatted) {
                                    clearErrors('postalCode');
                                } else if (!isPostalCodeValid(formatted, selectedCountry)) {
                                    setError('postalCode', { message: getPostalCodeError(selectedCountry) });
                                } else {
                                    clearErrors('postalCode');
                                }

                                // Adjust caret position
                                setTimeout(() => {
                                    let newCursorPos = selectionStart;

                                    // If inserting space automatically (Canada only)
                                    if (!isUSA && !isDeletingSpace && cleaned.length === 4 && selectionStart === 4) {
                                        newCursorPos += 1;
                                    }

                                    // If user deleted the space, move cursor back
                                    if (isDeletingSpace) {
                                        newCursorPos -= 1;
                                    }

                                    input.setSelectionRange(newCursorPos, newCursorPos);
                                }, 0);
                            }}
                        />
                    )}
                />
            </Grid>
        </Grid>
    );
};

export default AdditionalDetailsForm;
